import React from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { LockOutlined, LogoutOutlined, UserOutlined } from '@ant-design/icons';
import { useAuth } from '../../../contexts/AuthProvider';
import { useNavigate } from 'react-router-dom';
import { ADMIN_ROLES } from '../../../constants/roles';
import {
  getSessionDisplayEmail,
  getSessionDisplayName,
} from '../../../utils/sessionDisplayName';

const AccessDenied: React.FC = () => {
  const { logout, user } = useAuth();
  const { user: auth0User } = useAuth0();
  const navigate = useNavigate();

  const displayName = getSessionDisplayName(auth0User, user);
  const displayEmail = getSessionDisplayEmail(auth0User, user);

  const handleLogout = async () => {
    try {
      await logout({ openUrl: false });
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 px-4">
      <div className="w-full max-w-md text-center">
        {/* Icon */}
        <div className="mx-auto w-16 h-16 rounded-full border-[1.5px] border-slate-700 flex items-center justify-center mb-6">
          <LockOutlined className="text-2xl text-slate-500" />
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-white mb-2">
          Access Denied
        </h1>
        <p className="text-sm text-slate-400 mb-8">
          Your account does not have a role assigned for this portal.
        </p>

        {/* User card */}
        <div className="rounded-xl border border-slate-800 bg-slate-800/50 p-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full border border-slate-700 bg-slate-800 flex items-center justify-center flex-shrink-0">
              {user?.profile_image ? (
                <img src={user.profile_image} alt="Profile" className="w-full h-full rounded-full object-cover" />
              ) : (
                <UserOutlined className="text-slate-500" />
              )}
            </div>
            <div className="text-left min-w-0">
              <p className="text-sm font-medium text-white truncate">
                {displayName}
              </p>
              <p className="text-xs text-slate-400 truncate">
                {displayEmail ?? '—'}
              </p>
            </div>
          </div>
        </div>

        {/* Required roles */}
        <div className="rounded-xl border border-slate-800 p-4 mb-8 text-left">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">
            Required Role
          </p>
          <div className="flex flex-wrap gap-2">
            {ADMIN_ROLES.map((role) => (
              <span
                key={role}
                className="inline-flex px-2.5 py-1 text-xs font-medium rounded-full bg-slate-800 text-slate-300 border border-slate-700"
              >
                {role}
              </span>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-3">
            Contact your team lead to request access.
          </p>
        </div>

        {/* Sign out */}
        <button
          onClick={handleLogout}
          className="inline-flex items-center justify-center gap-2 w-full px-4 py-2.5 text-sm font-medium rounded-lg border-[1.5px] border-slate-700 text-slate-200 bg-slate-800 hover:border-slate-500 transition-colors"
        >
          <LogoutOutlined />
          Sign Out
        </button>
      </div>
    </div>
  );
};

export default AccessDenied;
